import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  Box, Button, Chip, CircularProgress, IconButton, Input, Modal, ModalClose, ModalDialog,
  Stack, Switch, Table, Typography, FormControl, FormLabel,
} from "@mui/joy";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";

import { useAdmin } from "../../AdminContext";
import {
  APIAdminQuizzesList, APIAdminQuizCreate, APIAdminQuizDelete, APIAdminQuizUpdate,
} from "../../api_admin";

export default function AdminQuizzesPage() {
  const { adminToken, canEdit, isSuperadmin } = useAdmin();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState({ code: "", title: "", category: "", order: 0, is_onboarding: false });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await APIAdminQuizzesList(adminToken);
      setItems(data.items || []);
    } catch (e) { /* handled */ } finally { setLoading(false); }
  };

  useEffect(() => { if (adminToken) load(); /* eslint-disable-next-line */ }, [adminToken]);

  const createQuiz = async () => {
    setError(null);
    if (!draft.code || !draft.title) { setError("Заполните код и название"); return; }
    try {
      setSaving(true);
      const res = await APIAdminQuizCreate(adminToken, draft);
      setCreateOpen(false);
      setDraft({ code: "", title: "", category: "", order: 0, is_onboarding: false });
      if (res?.id) navigate(`/admin/quizzes/${res.id}`);
      else load();
    } catch (e) {
      setError(e?.response?.data?.error || "Не удалось создать");
    } finally { setSaving(false); }
  };

  const toggleActive = async (q) => {
    try {
      await APIAdminQuizUpdate(adminToken, q.id, { is_active: !q.is_active });
      setItems((prev) => prev.map((x) => (x.id === q.id ? { ...x, is_active: !x.is_active } : x)));
    } catch (e) { /* ignore */ }
  };

  const removeQuiz = async (q) => {
    if (!window.confirm(`Удалить тест «${q.title}»? Действие необратимо.`)) return;
    try {
      await APIAdminQuizDelete(adminToken, q.id);
      setItems((prev) => prev.filter((x) => x.id !== q.id));
    } catch (e) { /* ignore */ }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography level="h3">Тесты</Typography>
        {canEdit && (
          <Button startDecorator={<AddRoundedIcon />} onClick={() => setCreateOpen(true)}>Создать тест</Button>
        )}
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>
      ) : items.length === 0 ? (
        <Typography sx={{ py: 6, textAlign: "center", color: "text.tertiary" }}>Тестов пока нет</Typography>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table hoverRow sx={{ "--TableCell-height": "52px", minWidth: 720 }}>
            <thead>
              <tr>
                <th style={{ width: "26%" }}>Название</th>
                <th>Код</th>
                <th>Категория</th>
                <th style={{ textAlign: "center" }}>Вопросов</th>
                <th style={{ textAlign: "center" }}>Начали</th>
                <th style={{ textAlign: "center" }}>Прошли</th>
                <th style={{ textAlign: "center" }}>Активен</th>
                <th style={{ textAlign: "right" }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {items.map((q) => (
                <tr key={q.id}>
                  <td>
                    <Typography level="body-sm" sx={{ fontWeight: 700 }}>{q.title}</Typography>
                    {q.is_onboarding && <Chip size="sm" variant="soft" color="primary">онбординг</Chip>}
                  </td>
                  <td><Typography level="body-xs" sx={{ fontFamily: "monospace" }}>{q.code}</Typography></td>
                  <td><Typography level="body-sm">{q.category || "—"}</Typography></td>
                  <td style={{ textAlign: "center" }}>{q.questions_count}</td>
                  <td style={{ textAlign: "center" }}>{q.started_count}</td>
                  <td style={{ textAlign: "center" }}>{q.completed_count}</td>
                  <td style={{ textAlign: "center" }}>
                    <Switch size="sm" checked={!!q.is_active} onChange={() => toggleActive(q)} disabled={!canEdit} />
                  </td>
                  <td>
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <IconButton size="sm" variant="soft" color="primary" onClick={() => navigate(`/admin/quizzes/${q.id}`)} title="Редактировать">
                        <EditRoundedIcon />
                      </IconButton>
                      <IconButton size="sm" variant="soft" color="neutral" onClick={() => navigate(`/admin/quizzes/${q.id}/stats`)} title="Статистика">
                        <BarChartRoundedIcon />
                      </IconButton>
                      {isSuperadmin && (
                        <IconButton size="sm" variant="soft" color="danger" onClick={() => removeQuiz(q)} title="Удалить">
                          <DeleteRoundedIcon />
                        </IconButton>
                      )}
                    </Stack>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Box>
      )}

      <Modal open={createOpen} onClose={() => setCreateOpen(false)}>
        <ModalDialog sx={{ width: { xs: "94%", sm: 460 }, borderRadius: "xl" }}>
          <ModalClose />
          <Typography level="title-lg" sx={{ mb: 1 }}>Новый тест</Typography>
          <Stack spacing={1.5}>
            <FormControl>
              <FormLabel>Код (латиница, уникальный)</FormLabel>
              <Input value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} placeholder="love_lang" />
            </FormControl>
            <FormControl>
              <FormLabel>Название</FormLabel>
              <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="Языки любви" />
            </FormControl>
            <FormControl>
              <FormLabel>Категория</FormLabel>
              <Input value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} placeholder="Отношения" />
            </FormControl>
            <FormControl>
              <FormLabel>Порядок</FormLabel>
              <Input type="number" value={draft.order} onChange={(e) => setDraft({ ...draft, order: Number(e.target.value) })} />
            </FormControl>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography level="body-sm">Показывать в онбординге</Typography>
              <Switch checked={draft.is_onboarding} onChange={(e) => setDraft({ ...draft, is_onboarding: e.target.checked })} />
            </Stack>
            {error && <Typography level="body-sm" color="danger">{error}</Typography>}
            <Button loading={saving} onClick={createQuiz}>Создать и открыть редактор</Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </Box>
  );
}